import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingDown, TrendingUp, Minus, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { useState, useEffect } from 'react';
import { ILDetailModal } from './ILDetailModal';
import { useAnalytics } from '@/hooks/useAnalytics';

interface ILCardProps {
  score: number;
  bucket: 'low' | 'moderate' | 'high';
  contributors: string[];
  trend7d?: number;
  trend30d?: number;
  sparklineData?: { date: string; score: number }[];
  onRefresh?: () => void;
}

export function ILCard({
  score,
  bucket,
  contributors,
  trend7d,
  trend30d,
  sparklineData = [],
  onRefresh
}: ILCardProps) {
  const [showDetail, setShowDetail] = useState(false);
  const { trackFeatureUsage } = useAnalytics();

  useEffect(() => {
    trackFeatureUsage('il_card', 'viewed', { score, bucket });
  }, [score, bucket]);

  const bucketConfig = {
    low: { label: 'Low', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    moderate: { label: 'Moderate', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
    high: { label: 'High', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' }
  };

  const contributorLabels: Record<string, string> = {
    diet: 'Diet',
    sleep: 'Sleep',
    stress: 'Stress',
    hrv: 'HRV',
    activity: 'Activity',
    alcohol: 'Alcohol'
  };

  const getTrendIcon = (trend?: number) => {
    if (!trend) return <Minus className="w-3 h-3" />;
    if (trend > 5) return <TrendingUp className="w-3 h-3 text-red-500" />;
    if (trend < -5) return <TrendingDown className="w-3 h-3 text-green-500" />;
    return <Minus className="w-3 h-3 text-muted-foreground" />;
  };

  return (
    <>
      <Card className="p-6 bg-gradient-to-br from-background to-muted/20">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-medium text-muted-foreground">
                Inflammatory Load (lifestyle)
              </h3>
              <Info className="w-4 h-4 text-muted-foreground cursor-help" />
            </div>
            <p className="text-xs text-muted-foreground">
              Today's score from your food, sleep, stress, activity, HRV and alcohol patterns
            </p>
          </div>
          <Badge className={bucketConfig[bucket].className}>
            {bucketConfig[bucket].label}
          </Badge>
        </div>

        <motion.div
          className="flex items-end gap-6 mb-6"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div>
            <div className="text-5xl font-bold text-foreground mb-1">
              {Math.round(score)}
            </div>
            <div className="text-xs text-muted-foreground">
              Out of 100
            </div>
          </div>

          {sparklineData.length > 0 && (
            <div className="flex-1 h-16">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparklineData}>
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>

        {trend7d !== undefined && (
          <div className="flex items-center gap-2 mb-4 text-sm">
            {getTrendIcon(trend7d)}
            <span className="text-muted-foreground">
              {Math.abs(trend7d).toFixed(1)} vs 7 days ago
            </span>
          </div>
        )}

        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">Top contributors</p>
          <div className="flex flex-wrap gap-2">
            {(contributors ?? []).map(contributor => (
              <Badge
                key={contributor}
                variant="outline"
                className="bg-background"
              >
                {contributorLabels[contributor] || contributor}
              </Badge>
            ))}
          </div>
        </div>

        <Button
          onClick={() => {
            setShowDetail(true);
            trackFeatureUsage('il_card', 'detail_opened', { score, bucket });
          }}
          className="w-full"
          variant="default"
        >
          See what to do
        </Button>
      </Card>

      <ILDetailModal
        open={showDetail}
        onClose={() => setShowDetail(false)}
        onRefresh={onRefresh}
      />
    </>
  );
}
